import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { AssetsService } from "../../../../../services/assets-service";
import { ToastrService } from "../../../../../services/toastr.service";
import { DownloadService } from "../../../../../services/download.service";
import { TaskService } from "../../../../../services/task.service";
import {
    ParentPermissionEnum,
    PermissionActionEnum,
    HomeCareUploadedDocumentationTypeEnum,
    homeCareUploadedDocumentationType
} from "../../../../../enums/enums";
import { enumToMap } from "../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { IBlobResponse } from "../../../../../resources-vue/vue-interfaces/i-hospice-client";
import {
    IDeleteHHHCTaskAttachmentRequest,
    IGetHHHCTaskAttachmentRequest,
    IHCTaskAttachmentOption,
    IHHHCTaskAsset
} from "../../../../../resources-vue/vue-interfaces/i-task";

import TableEmpty from "../../../../../resources-vue/vue-custom-elements/TableEmpty/TableEmpty.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import TablePrompt from "../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import VisitAttachmentsUploadHC from "./VisitAttachmentsUploadHC/VisitAttachmentsUploadHC.vue";

export default defineComponent({
    components: {
        TableEmpty,
        TypeaheadInput,
        TablePrompt
    },

    props: {
        visitDetails: { type: Object },
        isPlanner: { type: Boolean, default: false }
    },

    data() {
        return {
            fileDetails: [] as File[],
            attachmentToDelete: null as string,
            taskAttchments: [] as IHHHCTaskAsset[],
            taskAttachmentOptions: null as IHCTaskAttachmentOption,
            uploadedDocumentationType: enumToMap(homeCareUploadedDocumentationType),
            uploadedDocumentationTypeEnum: HomeCareUploadedDocumentationTypeEnum,
            loadingCount: 3,
            isDeleteInProgress: false,
            addPatientDocumentsPermission: false,
            editPatientDocumentsPermission: false,
            deletePatientDocumentsPermission: false,
            loadingAttachments: false,
            _taskService: null as TaskService,
            _assetsService: null as AssetsService,
            _downloadService: null as DownloadService,
            _permissionManager: null as PermissionManager,
            _toastrService: null as ToastrService
        };
    },

    created() {
        this._assetsService = Container.instance.get(AssetsService);
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._downloadService = Container.instance.get(DownloadService);
    },

    async mounted() {
        this.addPatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canAdd,
            this.visitDetails?.AgencyId
        );
        this.deletePatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canDelete,
            this.visitDetails?.AgencyId
        );
        this.editPatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canEdit,
            this.visitDetails?.AgencyId
        );
        await this.loadAttchments();
    },

    methods: {
        toggleAddAttachment() {
            this.$dialog.open(VisitAttachmentsUploadHC, {
                props: {
                    modal: true,
                    showHeader: false,
                    closeOnEscape: false
                },
                data: {
                    taskAttachmentOptions: this.taskAttachmentOptions,
                    visitDetails: this.visitDetails
                },
                onClose: (response) => {
                    if (response.data == "cancel") {
                        return;
                    } else {
                        this.loadAttchments();
                    }
                }
            });
        },

        requestDelete(documentId: string) {
            this.attachmentToDelete = documentId;
        },
        cancelDeleteAttachment() {
            this.attachmentToDelete = null;
        },
        async deleteSelectedDocument() {
            try {
                const payload: IDeleteHHHCTaskAttachmentRequest = {
                    taskId: this.visitDetails?.Id,
                    accountId: this.visitDetails?.AgencyId,
                    assetId: this.attachmentToDelete,
                    application: this.visitDetails?.Application,
                    patientId: this.visitDetails?.PatientId,
                    episodeId: this.visitDetails?.EpisodeId,
                    isForUser: false
                };
                this.isDeleteInProgress = true;
                await this._taskService.deleteHHHCTaskAttachment(payload);
                this.isDeleteInProgress = false;
                this.loadAttchments();
                this.attachmentToDelete = null;
                this._toastrService.success({
                    message: `The document has been successfully deleted.`,
                    title: `Document Deleted`
                });
            } catch (error) {
                console.error(error);
                this.isDeleteInProgress = false;
                this._toastrService.error({
                    title: `Delete Document Failed`,
                    message: `Delete document failed, Please contact axxess if issue still persists.`
                });
            }
        },

        extractAssetDownloadPayload(document: IHHHCTaskAsset) {
            return {
                assetId: document.AssetId,
                application: this.visitDetails?.Application,
                agencyId: this.visitDetails?.AgencyId
            };
        },

        async downloadDocuments(document: IHHHCTaskAsset) {
            const payload = this.extractAssetDownloadPayload(document);
            await this._assetsService
                .downloadPatientDocuments(payload)
                .then((response: any) => {
                    this._downloadService.downloadFile(response, response.type);
                    this._toastrService.success({
                        message: `The requested document is downloaded.`
                    });
                })
                .catch((error: any) => {
                    console.error(error);
                    this._toastrService.error({
                        title: `Downloading Document Failed`,
                        message: `Downloading document failed, Please contact axxess if issue still persists.`
                    });
                });
        },

        async openSelectedDocument(document: IHHHCTaskAsset) {
            const payload = this.extractAssetDownloadPayload(document);
            return await this._assetsService
                .downloadPatientChart(payload)
                .then((response: IBlobResponse) => {
                    const mimeType = this._downloadService.extractMimeTypeFromResponseHeader(response);
                    if (mimeType.includes("image") || mimeType.includes("pdf")) {
                        const url = window.URL.createObjectURL(
                            response.body instanceof Blob
                                ? response.body
                                : new Blob([response.body], {
                                      type: mimeType
                                  })
                        );
                        window.open(url, "_blank");
                    } else this._downloadService.downloadFile(response, null);
                    this._toastrService.success({
                        message: `The requested document is downloaded.`
                    });
                })
                .catch((error: any) => {
                    console.error(error);
                    this._toastrService.error({
                        title: `Opening Document Failed`,
                        message: `Opening document failed, Please contact axxess if issue still persists.`
                    });
                });
        },

        async loadAttchments() {
            this.loadingAttachments = true;
            try {
                const payload: IGetHHHCTaskAttachmentRequest = {
                    application: this.visitDetails?.Application,
                    agencyId: this.visitDetails?.AgencyId,
                    taskId: this.visitDetails?.Id,
                    episodeId: this.visitDetails?.EpisodeId,
                    patientId: this.visitDetails?.PatientId
                };
                const response = await this._taskService.getHHHCTaskAttachment(payload);
                const { TaskAssets, ...taskAttachmentOptions } = response;
                this.taskAttachmentOptions = taskAttachmentOptions;
                this.taskAttchments = TaskAssets;
            } catch (error) {
                console.error(error);
                this._toastrService.error({
                    title: `Error`,
                    message: `Error loading attachments, Please contact axxess if issue still persists.`
                });
            } finally {
                this.loadingAttachments = false;
            }
        }
    }
});
